'use client';

import React, { useState } from 'react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Check, Info, XIcon } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

interface PricingAdvantageTooltipProps {
  className?: string;
  planCredits?: number;
  planVideos?: number;
  costPerCredit?: number;
  currencySymbol?: string;
  children?: React.ReactNode;
}

export const PricingAdvantageTooltip = ({
  className,
  planCredits = 1350, // Default to Tier 3
  planVideos = 45, // Default to Tier 3
  costPerCredit = 22 / 1350, // Default to Tier 3 (~0.0163)
  currencySymbol = '£',
  children,
}: PricingAdvantageTooltipProps) => {
  const [open, setOpen] = useState(false);

  // Constants
  const CREDITS_PER_VIDEO = 30;
  const OPENAI_COST_PER_VIDEO = 0.75; // £0.75 based on $1.00

  // Calculations
  const zappingCostPerVideo = costPerCredit * CREDITS_PER_VIDEO; // e.g. 0.016 * 30 = 0.48
  const totalPlanCost = planCredits * costPerCredit; // e.g. 1350 * 0.016 = 22
  const openaiVideosForSameCost = Math.round(totalPlanCost / OPENAI_COST_PER_VIDEO); // e.g. 22 / 0.75 = 29.33 -> 29

  const percentMore = Math.round(
    ((planVideos - openaiVideosForSameCost) / openaiVideosForSameCost) * 100
  );
  const dialogId = React.useId();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <button
            type="button"
            id={`trigger-${dialogId}`}
            className={cn('inline-flex cursor-pointer items-center justify-center', className)}
            aria-label="See pricing comparison details"
          >
            <Info className="size-4 text-muted-foreground transition-colors hover:text-foreground" />
          </button>
        )}
      </DialogTrigger>
      <DialogContent
        className="z-[10000] w-[calc(100%-2rem)] max-w-[360px] border-0 bg-[#212120] p-3 pb-2 text-left text-white"
        showCloseButton={false}
        onOpenAutoFocus={(e) => e.preventDefault()}
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <DialogTitle>Sora 2 Price Verification</DialogTitle>
        </VisuallyHidden>

        <DialogClose className="absolute top-4 right-4 z-[10001] cursor-pointer rounded-full bg-white/10 p-2 text-white/70 transition-colors hover:bg-white/20 hover:text-white focus:outline-none">
          <XIcon className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <div className="mb-2 flex items-center gap-2 pt-1">
          <Info className="size-4 text-gray-400" />
          <span className="font-semibold text-gray-200 text-sm uppercase tracking-wide">
            Sora 2 Price Verification
          </span>
          <span className="rounded bg-white/10 px-1.5 py-0.5 font-bold text-[10px] text-gray-400 uppercase tracking-wide">
            {new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' }).format(
              new Date()
            )}
          </span>
        </div>

        <p className="mt-2 mb-1 text-center font-bold text-base text-white">
          Get {percentMore}% more Sora 2 videos
        </p>

        <div className="mb-2 space-y-3">
          {/* ZappingAI Row */}
          <div className="flex items-end gap-3">
            <div className="flex w-[50px] shrink-0 items-end">
              <div className="relative h-[18px] w-[48px]">
                <Image
                  src="/images/zappingai-logo-small.png"
                  alt="ZappingAI"
                  fill
                  sizes="48px"
                  className="!rounded-none object-contain object-left invert filter"
                  style={{ borderRadius: 0 }}
                  unoptimized
                />
              </div>
            </div>
            <div className="flex flex-grow items-center gap-2">
              <div className="relative h-4 flex-grow overflow-hidden rounded-sm bg-white/10">
                <div className="h-full w-full rounded-sm bg-green-500" />
                {/* Grid Lines Overlay */}
                <div className="absolute inset-0 z-10 grid h-full w-full grid-cols-5 divide-x divide-white/30">
                  <div />
                  <div />
                  <div />
                  <div />
                  <div />
                </div>
              </div>
              <span className="min-w-[60px] whitespace-nowrap text-right font-bold text-[10px] text-green-400">
                {percentMore}% MORE
              </span>
            </div>
          </div>

          {/* OpenAI Row */}
          <div className="flex items-center gap-3">
            <div className="flex w-[50px] shrink-0 items-center">
              <div className="relative h-3 w-[36px] opacity-80">
                <Image
                  src="/images/openai.png"
                  alt="OpenAI"
                  fill
                  sizes="36px"
                  className="!rounded-none object-contain object-left invert filter"
                  style={{ borderRadius: 0 }}
                />
              </div>
            </div>
            <div className="flex flex-grow items-center gap-2">
              <div className="relative h-4 flex-grow overflow-hidden rounded-sm bg-white/10">
                <div
                  className="h-full rounded-sm bg-white/40"
                  style={{ width: `${Math.round((openaiVideosForSameCost / planVideos) * 100)}%` }}
                />
                {/* Grid Lines Overlay */}
                <div className="absolute inset-0 z-10 grid h-full w-full grid-cols-5 divide-x divide-white/20">
                  <div />
                  <div />
                  <div />
                  <div />
                  <div />
                </div>
              </div>
              <span className="min-w-[60px]" />
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-2 text-gray-300 text-xs">
          <p className="mb-2 text-center font-bold text-base text-white">
            Lowest Cost Sora 2 Videos
          </p>

          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-4 gap-y-1.5 text-[11px]">
            <div className="font-bold text-white">Provider</div>
            <div className="text-center font-bold text-white">10s Video Cost</div>
            <div className="text-right font-bold text-white">
              {currencySymbol}
              {Math.round(totalPlanCost)} Output
            </div>

            <div className="relative h-[18px] w-[48px]">
              <Image
                src="/images/zappingai-logo-small.png"
                alt="ZappingAI"
                fill
                sizes="48px"
                className="!rounded-none object-contain object-left invert filter"
                style={{ borderRadius: 0 }}
                unoptimized
              />
            </div>
            <div className="text-center font-bold text-green-400">
              {currencySymbol}
              {zappingCostPerVideo.toFixed(2)}
            </div>
            <div className="flex items-center justify-end gap-1 font-bold text-green-400">
              <Check className="size-3.5" /> {planVideos} Videos
            </div>

            <div className="relative h-3 w-[36px] opacity-80">
              <Image
                src="/images/openai.png"
                alt="OpenAI"
                fill
                sizes="36px"
                className="!rounded-none object-contain object-left invert filter"
                style={{ borderRadius: 0 }}
              />
            </div>
            <div className="text-center text-gray-300">£{OPENAI_COST_PER_VIDEO.toFixed(2)}</div>
            <div className="text-right text-gray-300">{openaiVideosForSameCost} Videos</div>
          </div>

          <div className="my-2 h-px bg-white/20" />

          <div className="space-y-1 text-[10px] opacity-90">
            <p>
              <Link
                href="/pricing"
                className="!text-gray-400 hover:!text-white font-semibold underline decoration-gray-400 underline-offset-2"
              >
                ZappingAI:
              </Link>{' '}
              {planCredits} Credits Plan @ {currencySymbol}
              {Math.round(totalPlanCost)} = {planVideos} x {CREDITS_PER_VIDEO} credit videos ={' '}
              {currencySymbol}
              {Math.round(zappingCostPerVideo * 100) / 100} per video{' '}
              <a
                href="https://platform.openai.com/docs/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="!text-gray-400 hover:!text-white font-semibold underline decoration-gray-400 underline-offset-2"
              >
                <br />
                OpenAI:
              </a>{' '}
              $0.10/sec x 10s = $1.00 = £{OPENAI_COST_PER_VIDEO.toFixed(2)} per video
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
